<template>
  <div class="min-h-screen flex w-full bg-white">

    <!-- LADO IZQUIERDO: BRANDING (Igual al Login) -->
    <div class="hidden lg:flex w-1/2 bg-slate-900 relative overflow-hidden flex-col justify-between p-12 text-white">
      <div class="absolute inset-0 opacity-20">
        <svg class="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0 100 C 20 0 50 0 100 100 Z" fill="url(#grad)" />
          <defs>
            <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style="stop-color:#0B9257;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#020617;stop-opacity:1" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div class="relative z-10 flex items-center gap-3">
        <img src="@/assets/LOGO_NEXUS_WHITE.png" alt="Nexus Logo" class="transition-all duration-300 object-contain w-50" />
      </div>
      <div class="relative z-10 max-w-md">
        <blockquote class="text-2xl font-medium leading-relaxed mb-6">
          "La seguridad de tu información financiera es nuestra máxima prioridad."
        </blockquote>
        <div class="flex items-center gap-4">
          <div class="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center font-bold text-brand-400">NC</div>
          <div>
            <p class="font-semibold text-sm">Nexus Credit v3.0</p>
            <p class="text-slate-400 text-xs">Recuperación Segura</p>
          </div>
        </div>
      </div>
      <p class="relative z-10 text-xs text-slate-500">© {{ currentYear }} The Real Devs Pro. Todos los derechos reservados.</p>
    </div>

    <!-- LADO DERECHO: FORMULARIO DE RESTABLECIMIENTO -->
    <div class="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 relative">
      <div class="w-full max-w-md space-y-8">

        <div class="lg:hidden flex justify-center mb-8">
           <img
            src="@/assets/LOGO_NEXUS.png"
            alt="Nexus Logo"
            class="transition-all duration-300 object-contain w-50"
          />
        </div>

        <div class="text-center lg:text-left">
          <h1 class="text-3xl font-bold text-slate-900 tracking-tight">Restablecer Contraseña</h1>
          <p class="mt-2 text-slate-500">Crea una nueva contraseña segura para tu cuenta.</p>

          <div v-if="email" class="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300">
            <Mail class="w-3.5 h-3.5" /> {{ email }}
          </div>
        </div>

        <div v-if="errorMessage" class="p-4 rounded-lg bg-red-50 border border-red-100 flex items-center gap-3 text-red-600 animate-fade-in">
          <AlertCircle class="w-5 h-5 flex-shrink-0" />
          <span class="text-sm font-medium">{{ errorMessage }}</span>
        </div>

        <form @submit.prevent="handleResetPassword" class="space-y-6">

          <!-- NUEVA CONTRASEÑA -->
          <div class="space-y-1.5">
            <label class="text-sm font-semibold text-slate-700">Nueva Contraseña</label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock class="h-5 w-5 text-slate-400 group-focus-within:text-brand-500 transition-colors" />
              </div>
              <input
                v-model="form.newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                required
                class="form-input-premium pl-10 pr-10 no-uppercase"
                placeholder="Mínimo 6 caracteres"
              >
              <button
                type="button"
                @click="showNewPassword = !showNewPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <component :is="showNewPassword ? EyeOff : Eye" class="h-5 w-5" />
              </button>
            </div>
          </div>

          <!-- CONFIRMAR CONTRASEÑA -->
          <div class="space-y-1.5">
            <label class="text-sm font-semibold text-slate-700">Confirmar Contraseña</label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock class="h-5 w-5 text-slate-400 group-focus-within:text-brand-500 transition-colors" />
              </div>
              <input
                v-model="form.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                required
                class="form-input-premium pl-10 pr-10 no-uppercase"
                placeholder="Repite la contraseña"
              >
              <button
                type="button"
                @click="showConfirmPassword = !showConfirmPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <component :is="showConfirmPassword ? EyeOff : Eye" class="h-5 w-5" />
              </button>
            </div>
          </div>

          <!-- BOTÓN SUBMIT -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-brand-500/30 text-sm font-bold text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200"
          >
            <Loader2 v-if="isLoading" class="w-5 h-5 animate-spin mr-2" />
            <span v-else class="flex items-center">
              Restablecer y Acceder <ArrowRight class="ml-2 w-4 h-4" />
            </span>
          </button>
        </form>

        <p class="mt-6 text-center text-sm text-slate-500">
          ¿Recordaste tu contraseña?
          <router-link to="/login" class="font-semibold text-brand-600 hover:text-brand-500 transition-colors">
            Volver al Login
          </router-link>
        </p>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, AlertCircle } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import Swal from 'sweetalert2'; // Mantenemos Swal para las alertas bloqueantes de éxito/error crítico

const route = useRoute();
const router = useRouter();
const currentYear = new Date().getFullYear();

// Obtener datos de la URL
const token = route.query.token || null;
const email = route.query.email ? decodeURIComponent(route.query.email) : null;

// Estados
const form = ref({
  newPassword: '',
  confirmPassword: ''
});

const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const isLoading = ref(false);
const errorMessage = ref('');

// La clase para los inputs (para Tailwind v4)
const inputClass = "w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-all";

// Validación inicial al cargar la página
onMounted(() => {
  if (!token || !email) {
    Swal.fire({
      title: 'Enlace Inválido',
      text: 'El enlace de recuperación está incompleto o ha expirado.',
      icon: 'error',
      confirmButtonColor: '#0B9257',
    }).then(() => {
      router.push('/login');
    });
  }
});

// Proceso principal de restablecimiento y auto-login
const handleResetPassword = async () => {
  errorMessage.value = '';

  // 1. Validaciones locales
  if (form.value.newPassword !== form.value.confirmPassword) {
    errorMessage.value = 'Las contraseñas no coinciden.';
    return;
  }

  if (form.value.newPassword.length < 6) {
    errorMessage.value = 'La contraseña debe tener al menos 6 caracteres.';
    return;
  }

  isLoading.value = true;

  try {
    // 2. Enviar petición para cambiar contraseña
    const resetPayload = {
      token: token,
      email: email,
      new_password: form.value.newPassword
    };

    await apiClient.post('/password/reset', resetPayload);

    // 3. Auto-Login (reutilizando tu lógica de Login.vue)
    const loginPayload = {
      username: email,
      password: form.value.newPassword
    };

    const response = await apiClient.post('/login', loginPayload);
    const data = response.data;
    const accessToken = data.token;

    if (accessToken) {
      // A) Guardar Token
      localStorage.setItem('frontline_token', accessToken);

      // B) Guardar Usuario Encriptado
      const userToSave = {
        id: data.id,
        username: data.username,
        email: data.email,
        full_name: data.full_name,
        role_id: data.role_id,
        company: data.company,
        branch: data.branch,
        photo_url: data.photo_url
      };
      SecureStorage.setItem('llevo_user', userToSave);

      // C) Mostrar Éxito y Redirigir
      await Swal.fire({
        title: '¡Contraseña Actualizada!',
        text: 'Hemos iniciado tu sesión de forma segura.',
        icon: 'success',
        showConfirmButton: false,
        timer: 2000
      });

      router.push('/');
    } else {
      throw new Error('No se recibió token de acceso al auto-iniciar sesión.');
    }

  } catch (error) {
    console.error("Error en restablecimiento:", error);

    // Si el error viene del auto-login (Ej: 401)
    if (error.response?.config?.url.includes('/login')) {
      Swal.fire({
        title: 'Contraseña cambiada',
        text: 'Tu contraseña se actualizó, pero debes iniciar sesión manualmente.',
        icon: 'info',
        confirmButtonColor: '#0B9257'
      }).then(() => router.push('/login'));
      return;
    }

    // Si el error viene del restablecimiento (Ej: Token expirado o usado)
    errorMessage.value = error.response?.data?.message || 'El enlace ha expirado o es inválido.';
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in-up 0.3s ease-out forwards;
}
</style>